import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setCrossSiteThemeCookie } from '@shared/utils/crossSiteTheme';
import {
  THEME_STORAGE_KEY,
  applyThemeClassToHtml,
  type ThemeMode,
} from './themeUtils';

export type { ThemeMode } from './themeUtils';
export { THEME_STORAGE_KEY, resolveDocumentThemeClass, applyThemeClassToHtml } from './themeUtils';

interface ThemeState {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

function applyTheme(theme: ThemeMode) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  applyThemeClassToHtml(window.document.documentElement, theme, prefersDark);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
        setCrossSiteThemeCookie(theme);
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        applyTheme(newTheme);
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (rehydratedState, error) => {
        if (!error && rehydratedState?.theme) {
          applyTheme(rehydratedState.theme);
        }
      },
    }
  )
);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { theme } = useThemeStore();

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return <>{children}</>;
};

/** Tracks whether `document.documentElement` has `dark` after theme / system updates. */
export function useResolvedThemeIsDark(): boolean {
  const theme = useThemeStore((s) => s.theme);
  const [isDark, setIsDark] = React.useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  React.useEffect(() => {
    const update = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    update();
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', update);
    const obs = new window.MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      mq.removeEventListener('change', update);
      obs.disconnect();
    };
  }, [theme]);

  return isDark;
}
